import { useCallback, useRef } from 'react'

import { useEvents } from '../hooks/useEvents'

import { Box, Flex } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { throttle } from 'lodash'

import { Spinner } from '@/components/ui'

import { EventFiltersI } from '../types/types'
import { EventItem } from './EventItem'
import { TRACE_LOG_PANEL_HEIGHT } from './TraceLogContent'

const onEndReachedThreshold = 0.95

export const EventFeed = ({ incident, filters }: EventFiltersI) => {
  const ref = useRef<HTMLDivElement>(null)
  const { isLoading, events, getNewerEvents, getOlderEvents, hasOlder } =
    useEvents({
      incident,
      filters,
    })

  const onScroll = useCallback(
    throttle(async () => {
      if (ref?.current) {
        const { scrollTop, scrollHeight, clientHeight } = ref.current

        // Scrolled to the top
        if (scrollTop <= 0) {
          const hasMoreEvents = await getNewerEvents()
          if (hasMoreEvents) {
            ref?.current?.scrollTo({ top: 50, behavior: 'auto' })
          }
        }
        // Scrolled to the bottom
        const scrollPosition = scrollTop + clientHeight
        const endScrollThreshold = scrollHeight * onEndReachedThreshold
        if (scrollPosition >= endScrollThreshold && hasOlder) {
          await getOlderEvents()
        }
      }
    }, 200),
    [getNewerEvents]
  )

  return (
    <Flex
      direction='column'
      gap='8px'
      height={TRACE_LOG_PANEL_HEIGHT}
      onScroll={onScroll}
      overflowX='hidden'
      overflowY='auto'
      ref={ref}
    >
      <AnimatePresence>
        {events?.map((e) => (
          <motion.div
            animate={{ x: 0, opacity: 1 }}
            exit={{ opacity: 0 }}
            initial={{ x: -200, opacity: 0 }}
            key={e.node.id}
          >
            <EventItem
              event={e.node}
              key={e.node.id}
              selectedIncidentId={incident?.id}
              shouldShowDeviceInfo={Boolean(filters?.facilityId)}
            />
          </motion.div>
        ))}
      </AnimatePresence>
      {isLoading && (
        <Box
          alignContent='center'
          height={TRACE_LOG_PANEL_HEIGHT}
          pointerEvents='none'
          position='absolute'
          width='100%'
        >
          <Spinner size='lg' />
        </Box>
      )}
    </Flex>
  )
}
